import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { chip, chips, pane, sectionTitle, tile, tileK, tileV, tiles } from "./sheetStyles";

/**
 * A localizable text value: either a plain string (same in every language) or a
 * per-locale map. Numbers/units stay plain; free text (description, organoleptic
 * descriptors) can carry translations. `loc()` resolves it for the active locale.
 */
type LocStr = string | Record<string, string>;

type LimitRow = {
  parameter: LocStr;
  limit?: LocStr;
  typical?: LocStr;
  value?: LocStr;
  unit?: string;
  basis?: string;
  indent?: boolean;
};

type MicroRow = { parameter: LocStr; unit?: string; reference?: LocStr; method?: string };
type Allergen = { allergen: LocStr; ingredient?: boolean; traces?: boolean };

export type DatasheetData = {
  subtitle?: LocStr;
  issueRevision?: string;
  date?: string;
  description?: LocStr;
  ingredients?: LocStr;
  origin?: LocStr;
  shelfLife?: LocStr;
  gmoStatus?: LocStr;
  organoleptic?: Record<string, LocStr>;
  chemical?: LimitRow[];
  nutrition?: LimitRow[];
  microbiology?: MicroRow[];
  /** Footnote under the microbiology table, e.g. "limits apply to 5 units". */
  microbiologyNote?: LocStr;
  /** Further titled tables, e.g. an oil's toxic substances, pesticides, fatty acid composition. */
  extraTables?: { title: LocStr; rows: LimitRow[] }[];
  /** Application slugs (translated labels) or free text (e.g. an oil's uses after refining). */
  applications?: (string | Record<string, string>)[];
  suitability?: { yes?: string[]; no?: string[] };
  storage?: LocStr;
  packaging?: LocStr[];
  heavyMetals?: LimitRow[];
  mycotoxins?: LimitRow[];
  allergens?: Allergen[];
  compliance?: LocStr[];
  bseTse?: LocStr;
  legalNotice?: LocStr;
  /**
   * The downloadable PDFs, by asset filename: the language each is written in ("sr"/"en") and,
   * when a product has several (e.g. one per granulation), the button label.
   */
  pdfVariants?: { file: string; label?: string; lang?: string }[];
};

// Postgres jsonb does not keep object key order, so the data sheet's order is restored here.
const ORGANOLEPTIC_ORDER = ["appearance", "consistency", "smell", "taste", "color", "granulation"];
function organolepticRank(key: string): number {
  const i = ORGANOLEPTIC_ORDER.indexOf(key);
  return i < 0 ? ORGANOLEPTIC_ORDER.length : i;
}

function loc(v: LocStr | undefined, locale: string): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  return v[locale] ?? v.en ?? v.sr ?? Object.values(v)[0] ?? "";
}

/**
 * Full product data sheet rendered from a product's structured `datasheet`
 * attribute. Section titles, column headers and enumerable values (organoleptic
 * fields, suitability, applications) go through i18n; measured values come from
 * the data. Mirrors the catalog's visual language (same CSS tokens as the PDP).
 */
export default function ProductDatasheet({ data }: { data: DatasheetData }) {
  const { t, i18n } = useTranslation();
  const lc = i18n.resolvedLanguage ?? "en";
  const L = (v: LocStr | undefined) => loc(v, lc);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* intro badges */}
      {(data.ingredients || data.origin || data.shelfLife || data.gmoStatus) && (
        <div style={badges}>
          {data.ingredients && (
            <Badge k={t("datasheet.ingredients", "Ingredients")} v={L(data.ingredients)} />
          )}
          {data.origin && (
            <Badge k={t("datasheet.origin", "Country of origin")} v={L(data.origin)} />
          )}
          {data.shelfLife && (
            <Badge k={t("datasheet.shelfLife", "Shelf life")} v={L(data.shelfLife)} />
          )}
          {data.gmoStatus && (
            <Badge k={t("datasheet.gmo", "GMO status")} v={L(data.gmoStatus)} ok />
          )}
        </div>
      )}

      {data.description && (
        <p style={{ margin: "12px 0 0", color: "var(--ink-2)", lineHeight: 1.6 }}>{L(data.description)}</p>
      )}

      {data.organoleptic && Object.keys(data.organoleptic).length > 0 && (
        <>
          <Title>{t("datasheet.organoleptic", "Organoleptic properties")}</Title>
          <div style={tiles}>
            {Object.entries(data.organoleptic)
              .sort(([a], [b]) => organolepticRank(a) - organolepticRank(b))
              .map(([k, v]) => (
              <div key={k} style={tile}>
                <div style={tileK}>{t(`datasheet.org.${k}`, k)}</div>
                <div style={tileV}>{L(v)}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {data.chemical && data.chemical.length > 0 && (
        <>
          <Title>{t("datasheet.chemical", "Chemical characteristics")}</Title>
          <LimitTable rows={data.chemical} L={L} t={t} />
          {data.chemical.some((r) => r.basis) && (
            <div style={fine}>{t("datasheet.onDryBasis", "* on dry basis")}</div>
          )}
        </>
      )}

      {data.nutrition && data.nutrition.length > 0 && (
        <>
          <Title>{t("datasheet.nutrition", "Nutritional values (per 100 g)")}</Title>
          <table style={table}>
            <tbody>
              {data.nutrition.map((r, i) => (
                <tr key={i}>
                  <td style={{ ...tdP, ...(r.indent ? tdSub : {}) }}>{L(r.parameter)}</td>
                  <td style={tdNum}>{L(r.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {data.microbiology && data.microbiology.length > 0 && (
        <>
          <Title>{t("datasheet.microbiology", "Microbiological characteristics")}</Title>
          <MicroTable rows={data.microbiology} L={L} t={t} />
          {data.microbiologyNote && <div style={fine}>{L(data.microbiologyNote)}</div>}
        </>
      )}

      {data.extraTables?.map((x, i) => (
        <div key={i}>
          <Title>{L(x.title)}</Title>
          <LimitTable rows={x.rows} L={L} t={t} />
        </div>
      ))}

      {((data.applications && data.applications.length > 0) || data.suitability) && (
        <div style={cols2}>
          {data.applications && data.applications.length > 0 && (
            <div>
              <Title>{t("datasheet.applications", "Application")}</Title>
              <div style={chips}>
                {data.applications.map((a, i) => (
                  <span key={i} style={chip}>{typeof a === "string" ? t(`datasheet.app.${a}`, prettify(a)) : L(a)}</span>
                ))}
              </div>
            </div>
          )}
          {data.suitability && (
            <div>
              <Title>{t("datasheet.suitability", "Suitability")}</Title>
              <div style={yesno}>
                <SuitList kind="y" items={data.suitability.yes ?? []} t={t} />
                <SuitList kind="n" items={data.suitability.no ?? []} t={t} />
              </div>
            </div>
          )}
        </div>
      )}

      {((data.allergens && data.allergens.length > 0) || data.bseTse) && (
        <>
          <Title>{t("datasheet.allergens", "Allergens")}</Title>
          <div style={allergBox}>
            {data.allergens && data.allergens.length > 0 && (
              <>
                <span style={allergLab}>{t("datasheet.allergen.contains", "Contains")}:</span>{" "}
                <b>{data.allergens.filter((a) => a.ingredient).map((a) => L(a.allergen)).join(", ") || "—"}</b>
                {data.allergens.some((a) => a.traces && !a.ingredient) && (
                  <>
                    {" · "}
                    <span style={allergLab}>{t("datasheet.allergen.traces", "May contain traces")}:</span>{" "}
                    <b>{data.allergens.filter((a) => a.traces && !a.ingredient).map((a) => L(a.allergen)).join(", ")}</b>
                  </>
                )}
                {data.bseTse && " · "}
              </>
            )}
            {data.bseTse && <><span style={allergLab}>BSE/TSE</span> {L(data.bseTse)}</>}
          </div>
        </>
      )}

      {(data.storage || (data.packaging && data.packaging.length > 0)) && (
        <>
          <Title>{t("datasheet.storagePackaging", "Storage & packaging")}</Title>
          <div style={cols2}>
            {data.storage && (
              <div style={pane}>
                <div style={paneLab}>{t("datasheet.storage", "Storage")}</div>
                {L(data.storage)}
              </div>
            )}
            {data.packaging && data.packaging.length > 0 && (
              <div style={pane}>
                <div style={paneLab}>{t("datasheet.packaging", "Packaging")}</div>
                {data.packaging.map((p) => L(p)).join(" · ")}
              </div>
            )}
          </div>
        </>
      )}

      {((data.heavyMetals && data.heavyMetals.length > 0) || (data.mycotoxins && data.mycotoxins.length > 0)) && (
        <>
          <Title>{t("datasheet.contaminants", "Contaminants")}</Title>
          <div style={cols2}>
            {data.heavyMetals && data.heavyMetals.length > 0 && (
              <LimitTable head={t("datasheet.heavyMetals", "Heavy metals")} rows={data.heavyMetals} L={L} t={t} />
            )}
            {data.mycotoxins && data.mycotoxins.length > 0 && (
              <LimitTable head={t("datasheet.mycotoxins", "Mycotoxins")} rows={data.mycotoxins} L={L} t={t} />
            )}
          </div>
        </>
      )}

      {data.compliance && data.compliance.length > 0 && (
        <>
          <Title>{t("datasheet.compliance", "Regulatory compliance")}</Title>
          <ul style={fineList}>
            {data.compliance.map((c, i) => (
              <li key={i}>{L(c)}</li>
            ))}
          </ul>
        </>
      )}

      {data.legalNotice && (
        <footer style={footer}>
          {L(data.legalNotice)}
          {data.date && <div style={{ color: "var(--ink-3)", fontWeight: 600, marginTop: 6 }}>{t("datasheet.date", "Date")}: {data.date}</div>}
        </footer>
      )}
    </div>
  );
}

// ---- small pieces ----------------------------------------------------------

function Title({ children }: { children: React.ReactNode }) {
  return <h2 style={sectionTitle}>{children}</h2>;
}

function Badge({ k, v, ok }: { k: string; v: string; ok?: boolean }) {
  return (
    <div style={badge}>
      <span style={badgeK}>{k}</span>
      <span style={{ ...badgeV, ...(ok ? { color: "var(--ok)" } : {}) }}>{v}</span>
    </div>
  );
}

function SuitList({ kind, items, t }: { kind: "y" | "n"; items: string[]; t: TFunction }) {
  return (
    <div style={{ ...yn, ...(kind === "y" ? ynY : ynN) }}>
      <div style={{ ...ynHd, background: kind === "y" ? "var(--ok)" : "var(--err)" }}>
        {kind === "y" ? t("datasheet.yes", "Yes") : t("datasheet.no", "No")}
      </div>
      <ul style={ynUl}>
        {items.map((it) => (
          <li key={it} style={ynLi}>{t(`datasheet.suit.${it}`, prettify(it))}</li>
        ))}
      </ul>
    </div>
  );
}

function LimitTable({
  rows,
  L,
  t,
  head,
}: {
  rows: LimitRow[];
  L: (v: LocStr | undefined) => string;
  t: TFunction;
  head?: string;
}) {
  // A sheet may give only typical values (crude oil) or only limits; show just the columns it has.
  const hasLimit = rows.some((r) => r.limit);
  const hasTypical = rows.some((r) => r.typical);
  return (
    <div>
      <table style={table}>
        <thead>
          <tr>
            <th style={th}>{head ?? t("datasheet.col.parameter", "Parameter")}</th>
            {hasLimit && <th style={th}>{t("datasheet.col.limit", "Limit")}</th>}
            {hasTypical && <th style={th}>{t("datasheet.col.typical", "Typical")}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={tdP}>
                {L(r.parameter)}
                {r.basis ? "*" : ""}
              </td>
              {hasLimit && <td style={tdLim}>{L(r.limit)}</td>}
              {hasTypical && <td style={tdNum}>{L(r.typical)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MicroTable({ rows, L, t }: { rows: MicroRow[]; L: (v: LocStr | undefined) => string; t: TFunction }) {
  // Some sheets give only parameter + limit (lecithin), without unit or test method.
  const hasUnit = rows.some((r) => r.unit);
  const hasMethod = rows.some((r) => r.method);
  return (
    <table style={table}>
      <thead>
        <tr>
          <th style={th}>{t("datasheet.col.parameter", "Parameter")}</th>
          {hasUnit && <th style={th}>{t("datasheet.col.unit", "Unit")}</th>}
          <th style={th}>{t("datasheet.col.reference", "Reference value")}</th>
          {hasMethod && <th style={th}>{t("datasheet.col.method", "Method")}</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td style={tdP}>{L(r.parameter)}</td>
            {hasUnit && <td style={td}>{r.unit}</td>}
            <td style={tdNum}>{L(r.reference)}</td>
            {hasMethod && <td style={td}>{r.method}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function prettify(slug: string): string {
  const s = slug.replace(/[-_]/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ---- styles (same tokens as the PDP) --------------------------------------

const badges: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: 10, marginTop: 6 };
const badge: React.CSSProperties = {
  display: "flex", flexDirection: "column", gap: 1, background: "var(--surface)",
  border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: "8px 14px", minWidth: 120,
};
const badgeK: React.CSSProperties = { fontSize: 9.5, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--ink-4)" };
const badgeV: React.CSSProperties = { fontSize: 14, fontWeight: 600, color: "var(--ink)" };

const table: React.CSSProperties = {
  width: "100%", borderCollapse: "collapse", fontSize: 12.5, background: "var(--surface)",
  border: "1px solid var(--border)", borderRadius: "var(--r-sm)", overflow: "hidden",
};
const th: React.CSSProperties = {
  fontSize: 9.5, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--ink-4)", textAlign: "left",
  padding: "8px 11px", background: "var(--surface-2)", borderBottom: "1px solid var(--border)",
};
const td: React.CSSProperties = { padding: "7px 11px", borderBottom: "1px solid var(--border)", color: "var(--ink-2)" };
const tdP: React.CSSProperties = { ...td, color: "var(--ink)", fontWeight: 600 };
const tdNum: React.CSSProperties = { ...td, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const tdLim: React.CSSProperties = { ...td, color: "var(--ink-3)" };
const tdSub: React.CSSProperties = { paddingLeft: 24, color: "var(--ink-3)", fontWeight: 400 };

const cols2: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20, marginTop: 4 };

const yesno: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };
const yn: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "var(--r-md)", overflow: "hidden" };
const ynY: React.CSSProperties = {};
const ynN: React.CSSProperties = {};
const ynHd: React.CSSProperties = { padding: "7px 12px", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--on-primary)" };
const ynUl: React.CSSProperties = { margin: 0, padding: "10px 12px 10px 26px" };
const ynLi: React.CSSProperties = { fontSize: 12.5, color: "var(--ink-2)", margin: "2px 0" };

const allergBox: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: "12px 14px", fontSize: 12.5, color: "var(--ink-2)" };
const allergLab: React.CSSProperties = { fontSize: 9.5, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--ink-4)" };

const paneLab: React.CSSProperties = { fontSize: 9.5, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--ink-4)", marginBottom: 4 };

const fine: React.CSSProperties = { fontSize: 10.5, color: "var(--ink-4)", marginTop: 6 };
const fineList: React.CSSProperties = { fontSize: 10.5, color: "var(--ink-4)", lineHeight: 1.6, margin: "4px 0 0", paddingLeft: 18 };
const footer: React.CSSProperties = { marginTop: 24, paddingTop: 14, borderTop: "1px solid var(--border)", fontSize: 10.5, color: "var(--ink-4)", lineHeight: 1.6 };
